import React, { useEffect, useState } from 'react'

const KEY_ENTER = 'Enter'
const KEY_SPACE = 'Space'

export type ChipProps = {
  className?: string
  innerContentClassName?: string
  containerClassName?: string
  active?: boolean
  disabled?: boolean
  activeChanged: (isActive: boolean) => void
  onBlur?: (obj: React.FocusEvent<HTMLSpanElement>) => void
  onFocus?: (obj: React.FocusEvent<HTMLSpanElement>) => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
} & JSX.IntrinsicElements['div']

const Chip: React.FunctionComponent<ChipProps> = ({
  children,
  className,
  innerContentClassName,
  containerClassName,
  active,
  disabled,
  activeChanged,
  onBlur,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  ...props
}: ChipProps) => {
  const [isActive, setActive] = useState(active)

  const indicatorClassName = ['chip', isActive && 'chip--active', disabled && 'chip--disabled', className]
    .filter((classFilter) => !!classFilter)
    .join(' ')

  const handleChipClick = () => {
    if (disabled) {
      return
    }

    const newIsActive = !isActive
    setActive(newIsActive)
    activeChanged && activeChanged(newIsActive)
  }

  useEffect(() => setActive(active), [active])

  const handleChipKeyPress = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (!disabled && (event.key === KEY_ENTER || event.code === KEY_SPACE)) {
      handleChipClick()
      event.preventDefault()
    }
  }

  const chipAccessibilityProps = {
    role: 'button',
    tabIndex: 0,
    onKeyDown: handleChipKeyPress,
    onClick: handleChipClick,
    ...(onBlur && { onBlur }),
    ...(onFocus && { onFocus }),
    ...(onMouseEnter && { onMouseEnter }),
    ...(onMouseLeave && { onMouseLeave }),
  }

  return (
    <div className={`grid-item ${containerClassName || ''}`}>
      <div className={`${indicatorClassName} one-whole`} {...props} {...chipAccessibilityProps}>
        {children && <span className={`align-center ${innerContentClassName ?? ''}`}>{children}</span>}
      </div>
    </div>
  )
}

export default Chip
